import React from 'react';
import { PriceTag } from './PriceTag';
import { Badge } from './Badge';
import { UPCOMING_BADGE_LABEL } from '../../domain/property/formatters';
import type { PropertyBadge } from '../../domain/property/badges';

/**
 * PropertyCard — 부냥 단지 카드 통합 컴포넌트.
 *
 * variant:
 *   - compact: 메인 TOP3 가로 카드 (240px 너비)
 *   - horizontal: 리스트 페이지 가로 카드 (사진 120px + 본문)
 *   - wide: 지도 시트 / 큰 강조 카드
 *
 * 정책 강제:
 *   - 가격은 PriceTag 경유 (min/max 있으면 실가격, 없으면 "가격 문의")
 *   - 배지는 buildBadges(domain/property/badges.ts) 결과만 — 대표혜택 → 할인율 → '분양예정', 최대 3개.
 *     상태 단어(분양중·마감임박·임대·사전등록) 배지는 만들지 않는다. (v2 2026-09-21)
 *
 * v2 스타일(목업 .lcard / .card): radius 16 · --ca-sh-card · 테두리 없음 · 가격 .num 오렌지.
 */

export type PropertyCardVariant = 'compact' | 'horizontal' | 'wide';

export interface PropertyCardData {
  id: number | string;
  title: string;
  /** "달서구 본동" 형태의 위치 라벨 */
  locationLabel?: string;
  imageUrl?: string | null;
  /** 분양 상태 — 마스킹 후 표시 */
  status?: string | null;
  /** "분양예정" 배지 노출 여부 (status=분양예정 또는 is_pre_registration — isUpcomingBadge) */
  isPreRegistration?: boolean;
  /** 자료 부족 단지 — 회색조 + "자료 정리 중" 뱃지 */
  isSparse?: boolean;
  /** 메타 라인 (예: "1,200세대 · 84㎡") */
  metaLine?: string;
  /** @deprecated v2(2026-09-21) — badges(buildBadges) 로 대체. 카드에 렌더하지 않음 */
  benefitChips?: string[];
  /** 카드 상단 작은 라벨 (예: "월 검색 12,100", "분양예정", "자료 정리 중") */
  topLabel?: string;
  /** topLabel 색 — accent(주황 dark) 또는 muted (회색) */
  topLabelTone?: 'accent' | 'muted';
  /** 최저 분양가 (만원). 정책상 PriceTag 가 처리. */
  minPrice?: number | null;
  /** 최고 분양가 (만원). */
  maxPrice?: number | null;
  /** v2 배지 — buildBadges(property) 결과 (최대 3). 미지정 시 isPreRegistration 이면 '분양예정'만 */
  badges?: PropertyBadge[];
  /** 평당가(만원) — 서버 price_per_pyeong. 있을 때만 우측 표기 */
  pricePerPyeong?: number | null;
}

export interface PropertyCardProps {
  data: PropertyCardData;
  variant?: PropertyCardVariant;
  onClick?: () => void;
  /** 비교 선택 체크박스 활성 — list 페이지에서만 */
  selectable?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
  className?: string;
}

const ImageBox: React.FC<{ src?: string | null; sparse?: boolean; rounded: string; aspect: string }> = ({
  src, sparse, rounded, aspect,
}) => (
  <div className={`relative overflow-hidden bg-block ${rounded} ${aspect}`}>
    {src ? (
      <img
        src={src}
        alt=""
        loading="lazy"
        className={`h-full w-full object-cover ${sparse ? 'grayscale opacity-70' : ''}`}
      />
    ) : (
      <div className="flex h-full items-center justify-center text-mute2 text-[10.5px] uppercase tracking-widest font-mono">
        no image
      </div>
    )}
    {sparse && (
      <span className="absolute left-2 top-2 rounded-ca-sm bg-frame/92 px-2 py-1 text-[10.5px] font-bold text-muted backdrop-blur">
        자료 정리 중
      </span>
    )}
  </div>
);

/** 카드 배지 — buildBadges 결과 우선, 없으면 레거시 isPreRegistration → '분양예정' 만 */
const resolveBadges = (data: PropertyCardData): PropertyBadge[] => {
  if (data.badges) return data.badges.slice(0, 3);
  return data.isPreRegistration
    ? [{ key: 'upcoming', label: UPCOMING_BADGE_LABEL, tone: 'o', ariaLabel: '분양 상태' }]
    : [];
};

const BadgeRow: React.FC<{ badges: PropertyBadge[]; className?: string }> = ({ badges, className = '' }) => {
  if (badges.length === 0) return null;
  return (
    <div className={`flex flex-wrap items-center gap-1 ${className}`}>
      {badges.map((b) => (
        <Badge key={b.key} tone={b.tone} size="tag" ariaLabel={b.ariaLabel}>
          {b.label}
        </Badge>
      ))}
    </div>
  );
};

/** 가격 + 평당가 행 (목업 .lcard .between) */
const PriceRow: React.FC<{ data: PropertyCardData; className?: string }> = ({ data, className = '' }) => (
  <div className={`flex items-baseline justify-between gap-2 ${className}`}>
    <PriceTag size="md" minPrice={data.minPrice} maxPrice={data.maxPrice} className="num !text-accent" />
    {typeof data.pricePerPyeong === 'number' && data.pricePerPyeong > 0 && (
      <span className="num shrink-0 text-[11px] font-bold text-mute2">
        평당 {data.pricePerPyeong.toLocaleString()}만
      </span>
    )}
  </div>
);

export const PropertyCard: React.FC<PropertyCardProps> = ({
  data,
  variant = 'compact',
  onClick,
  selectable,
  selected,
  onToggleSelect,
  className = '',
}) => {
  const badges = resolveBadges(data);
  const titleClass = 'text-[16px] font-semibold leading-[22px] text-ink line-clamp-2';
  const subClass = 'text-[13px] leading-[18px] text-muted mt-1';
  const cardBase = `relative overflow-hidden rounded-ca-3xl bg-frame shadow-ca-card ${
    data.isSparse ? 'opacity-90' : ''
  }`;

  if (variant === 'horizontal') {
    // 목업 .lcard — padding 12 · gap 12 · 썸네일 104×88 radius 12
    const topLabelClass = data.topLabelTone === 'muted' ? 'text-muted' : 'text-accent-dark';
    return (
      <article
        className={`relative flex gap-3 rounded-ca-3xl bg-frame p-3 shadow-ca-card ${
          data.isSparse ? 'bg-ca-surface' : ''
        } ${className}`}
        onClick={onClick}
      >
        <div className="relative h-[88px] w-[104px] shrink-0 overflow-hidden rounded-ca-xl bg-ca-surface">
          {data.imageUrl ? (
            <img
              src={data.imageUrl}
              alt=""
              loading="lazy"
              className={`h-full w-full object-cover ${data.isSparse ? 'grayscale opacity-70' : ''}`}
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center bg-block"
              style={
                data.isSparse
                  ? { backgroundImage: 'repeating-linear-gradient(135deg, transparent 0 10px, rgba(148,163,184,.18) 10px 11px)' }
                  : undefined
              }
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="h-7 w-7 text-mute2"
                aria-hidden
              >
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
              </svg>
            </div>
          )}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <BadgeRow badges={badges} />
          {data.topLabel && (
            <div className={`text-[11px] font-bold ${topLabelClass}`}>{data.topLabel}</div>
          )}
          <h3 className="text-[16px] font-semibold leading-[22px] text-ink line-clamp-2">
            {data.title}
          </h3>
          {data.locationLabel && (
            <p className="text-[13px] leading-[18px] text-muted">{data.locationLabel}</p>
          )}
          {data.metaLine && (
            <p className="text-[11px] text-mute2">{data.metaLine}</p>
          )}
          {/* 분양가 — 구매자 1순위 스캔 정보 (2026-07-07 카드 위계 개선) */}
          <PriceRow data={data} className="mt-auto" />
        </div>
        {selectable && (
          <button
            type="button"
            aria-label="비교 선택"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onToggleSelect?.();
            }}
            className={`absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-ca-sm border ${
              selected ? 'border-accent bg-accent text-white' : 'border-ca-line-strong bg-frame'
            }`}
          >
            {selected && <span className="text-[12px] font-bold">✓</span>}
          </button>
        )}
      </article>
    );
  }

  if (variant === 'wide') {
    return (
      <article className={`${cardBase} ${className}`} onClick={onClick}>
        <ImageBox src={data.imageUrl} sparse={data.isSparse} rounded="rounded-none" aspect="aspect-[16/10]" />
        <div className="p-4">
          <BadgeRow badges={badges} className="mb-1.5" />
          <h3 className={titleClass}>{data.title}</h3>
          {data.locationLabel && <p className={subClass}>{data.locationLabel}</p>}
          {data.metaLine && (
            <p className="mt-1 text-[11px] text-mute2">{data.metaLine}</p>
          )}
          <PriceRow data={data} className="mt-3" />
        </div>
      </article>
    );
  }

  // compact (기본) — 사진 위 배지(최대 3) / 없으면 topLabel(검색량 등), 본문은 제목·위치·메타·가격
  return (
    <article className={`${cardBase} w-[240px] shrink-0 ${className}`} onClick={onClick}>
      <div className="relative">
        <ImageBox src={data.imageUrl} sparse={data.isSparse} rounded="rounded-none" aspect="aspect-[4/3]" />
        {badges.length > 0 ? (
          <BadgeRow badges={badges} className="absolute left-2.5 top-2.5 right-2.5" />
        ) : data.topLabel ? (
          <span
            className={`absolute left-2.5 top-2.5 rounded-ca-sm px-2 py-1 text-[11px] font-bold backdrop-blur ${
              data.topLabelTone === 'muted'
                ? 'bg-frame/92 text-muted'
                : 'bg-frame/92 text-ink'
            }`}
          >
            {data.topLabel}
          </span>
        ) : null}
      </div>
      <div className="px-3.5 pb-3.5 pt-2.5">
        <h3 className={titleClass}>{data.title}</h3>
        {data.locationLabel && <p className={subClass}>{data.locationLabel}</p>}
        {data.metaLine && (
          <p className="mt-1 text-[11px] text-mute2 line-clamp-1">{data.metaLine}</p>
        )}
        {/* 분양가 — 카드 하단 강조 (2026-07-07) */}
        <PriceRow data={data} className="mt-2 border-t border-ca-line-soft pt-2" />
      </div>
    </article>
  );
};

export default PropertyCard;
